import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { ShoppingCart, Heart, User, LogOut, ChevronDown, Menu, X, Search } from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const translations = {
  en: {
    home: 'Home',
    shop: 'Shop',
    about: 'About',
    blog: 'Blog',
    contact: 'Contact',
    admin: 'Admin',
    login: 'Sign In / Register',
    logout: 'Logout',
    myOrders: 'My Orders'
  },
  tr: {
    home: 'Ana Sayfa',
    shop: 'Mağaza',
    about: 'Hakkımızda',
    blog: 'Blog',
    contact: 'İletişim',
    admin: 'Yönetici',
    login: 'Giriş / Kayıt',
    logout: 'Çıkış',
    myOrders: 'Siparişlerim'
  },
  el: {
    home: 'Αρχική',
    shop: 'Κατάστημα',
    about: 'Σχετικά',
    blog: 'Ιστολόγιο',
    contact: 'Επικοινωνία',
    admin: 'Διαχειριστής',
    login: 'Είσοδος / Εγγραφή',
    logout: 'Αποσύνδεση',
    myOrders: 'Οι Παραγγελίες μου'
  },
  de: {
    home: 'Startseite',
    shop: 'Geschäft',
    about: 'Über uns',
    blog: 'Blog',
    contact: 'Kontakt',
    admin: 'Administrator',
    login: 'Anmelden / Registrieren',
    logout: 'Abmelden',
    myOrders: 'Meine Bestellungen'
  }
};

const languages = [
  { code: 'en', flag: '🇬🇧', name: 'English' },
  { code: 'tr', flag: '🇹🇷', name: 'Türkçe' },
  { code: 'el', flag: '🇬🇷', name: 'Ελληνικά' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' }
];

const Header = () => {
  const navigate = useNavigate();
  const { user, logout, cart, wishlistIds, language, setLanguage, displayCurrency, setDisplayCurrency, openCartDrawer, openAuthModal } = useContext(AppContext);
  const t = translations[language] || translations.en;
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMiniCartOpen, setIsMiniCartOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchResults([]);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setIsLangMenuOpen(false);
        setShowSearch(false);
        setSearchResults([]);
        setIsMiniCartOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleSearchInput = (value) => {
    setSearchQuery(value);
    clearTimeout(searchTimerRef.current);
    if (value.length < 2) { setSearchResults([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/products/search-suggestions?q=${encodeURIComponent(value)}`);
        setSearchResults(res.data);
      } catch { setSearchResults([]); }
    }, 300);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchResults([]);
      setShowSearch(false);
    }
  };

  const currentLang = languages.find(l => l.code === language) || languages[0];

  const handleLogin = () => {
    openAuthModal('signin');
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <header className="header-nav">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/" className="cursor-pointer flex items-center gap-2 md:gap-4" data-testid="logo">
              <img
                src="https://customer-assets.emergentagent.com/job_philately-shop/artifacts/81ter58o_77df6711-591a-4e86-9122-0f8adb5c4777.png"
                alt="North Cyprus Philately Logo"
                className="h-12 w-12 md:h-16 md:w-16"
              />
              <div>
                <h1 className="text-lg md:text-2xl font-bold">North Cyprus Philately</h1>
                <p className="text-xs font-light hidden sm:block">Collecting History, One Stamp at a Time</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="hover:text-[#C8A951] transition" data-testid="nav-home">{t.home}</Link>
            <Link to="/shop" className="shop-link" data-testid="nav-shop">{t.shop}</Link>
            <Link to="/about" className="hover:text-[#C8A951] transition" data-testid="nav-about">{t.about}</Link>
            <Link to="/blog" className="hover:text-[#C8A951] transition" data-testid="nav-blog">{t.blog}</Link>
            <Link to="/faq" className="hover:text-[#C8A951] transition" data-testid="nav-faq">FAQ</Link>
            <Link to="/contact" className="hover:text-[#C8A951] transition" data-testid="nav-contact">{t.contact}</Link>
            <Link to="/buyer/orders" className="hover:text-[#C8A951] transition font-semibold" data-testid="nav-orders">{t.myOrders}</Link>
            {user && user.role === 'admin' && <Link to="/admin" className="hover:text-[#C8A951] transition" data-testid="nav-admin">{t.admin}</Link>}
            
            {/* Search */}
            <div className="relative" ref={searchRef}>
              {showSearch ? (
                <form onSubmit={handleSearchSubmit} className="flex items-center">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchInput(e.target.value)}
                    placeholder={language === 'tr' ? 'Ara...' : 'Search...'}
                    className="w-40 lg:w-56 px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
                    autoFocus
                  />
                  <button type="button" onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults([]); }} className="ml-1 text-gray-400 hover:text-gray-600">
                    <X size={16} />
                  </button>
                </form>
              ) : (
                <button onClick={() => setShowSearch(true)} className="hover:text-[#C8A951] transition" aria-label="Search">
                  <Search size={20} />
                </button>
              )}
              {searchResults.length > 0 && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border z-50 max-h-80 overflow-y-auto">
                  {searchResults.map(p => (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition"
                      onClick={() => { navigate(`/product/${p.id}`); setSearchResults([]); setSearchQuery(''); setShowSearch(false); }}
                    >
                      {p.images?.[0] && (
                        <img src={p.images[0].startsWith('http') ? p.images[0] : `${BACKEND_URL}${p.images[0]}`} alt="" className="w-10 h-10 object-cover rounded" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.title}</p>
                        <p className="text-xs text-[#B8941E] font-bold">${p.discounted_price && p.discounted_price < p.price ? p.discounted_price : p.price}</p>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => { navigate(`/shop?search=${encodeURIComponent(searchQuery)}`); setSearchResults([]); setSearchQuery(''); setShowSearch(false); }}
                    className="w-full text-center text-sm text-[#C8A951] hover:bg-gray-50 py-2 border-t font-semibold"
                  >
                    {language === 'tr' ? 'Tüm sonuçları gör' : 'View all results'}
                  </button>
                </div>
              )}
            </div>

            {/* Language Dropdown */}
            <div className="relative" data-testid="language-switcher">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition"
              >
                <span className="text-2xl">{currentLang.flag}</span>
                <span className="font-semibold">{currentLang.code.toUpperCase()}</span>
                <ChevronDown size={16} className={`transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLangMenuOpen && (
                <>
                  {/* Backdrop */}
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setIsLangMenuOpen(false)}
                  />
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setIsLangMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 transition ${
                          language === lang.code ? 'bg-gray-50 font-semibold' : ''
                        }`}
                      >
                        <span className="text-2xl">{lang.flag}</span>
                        <div className="flex-1 text-left">
                          <div className="text-sm">{lang.name}</div>
                          <div className="text-xs text-gray-500">{lang.code.toUpperCase()}</div>
                        </div>
                        {language === lang.code && (
                          <span className="text-[#C8A951]">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Currency Selector */}
            <select
              value={displayCurrency}
              onChange={(e) => setDisplayCurrency(e.target.value)}
              className="text-xs border rounded px-1.5 py-1 bg-white hover:border-[#C8A951] focus:ring-1 focus:ring-[#C8A951] cursor-pointer"
              aria-label="Currency"
            >
              <option value="USD">$ USD</option>
              <option value="EUR">€ EUR</option>
              <option value="GBP">£ GBP</option>
            </select>

            <Link to="/wishlist" className="relative hover:text-[#C8A951] transition" aria-label="Wishlist">
              <Heart size={22} className={wishlistIds.length > 0 ? 'fill-red-500 text-red-500' : ''} aria-hidden="true" />
              {wishlistIds.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {wishlistIds.length}
                </span>
              )}
            </Link>

            <div className="relative"
              onMouseEnter={() => setIsMiniCartOpen(true)}
              onMouseLeave={() => setIsMiniCartOpen(false)}
            >
              <button onClick={() => openCartDrawer()} className="relative hover:text-[#C8A951] transition" data-testid="cart-btn" aria-label={`Shopping cart${cartCount > 0 ? `, ${cartCount} items` : ''}`}>
                <ShoppingCart size={24} aria-hidden="true" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#C8A951] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </button>
              {isMiniCartOpen && cart.length > 0 && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border z-50 p-4">
                  <div className="max-h-60 overflow-y-auto space-y-3">
                    {cart.slice(0, 5).map(item => (
                      <div key={item.id} className="flex items-center gap-3">
                        {item.product?.images?.[0] && (
                          <img
                            src={item.product.images[0].startsWith('http') ? item.product.images[0] : `${window.location.origin}${item.product.images[0]}`}
                            alt="" className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.product?.[`title_${language}`] || item.product?.title}</p>
                          <p className="text-xs text-gray-500">{item.quantity} x ${item.product?.price?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                    {cart.length > 5 && <p className="text-xs text-gray-400 text-center">+{cart.length - 5} more</p>}
                  </div>
                  <div className="border-t mt-3 pt-3">
                    <div className="flex justify-between font-semibold text-sm mb-3">
                      <span>{language === 'tr' ? 'Toplam' : 'Total'}</span>
                      <span style={{color: '#B8941E'}}>${cart.reduce((s, i) => s + (i.product?.price || 0) * i.quantity, 0).toFixed(2)}</span>
                    </div>
                    <Link to="/cart" onClick={() => setIsMiniCartOpen(false)} className="btn-primary w-full text-sm text-center block">
                      {language === 'tr' ? 'Ödemeye Geç' : language === 'de' ? 'Zur Kasse' : language === 'el' ? 'Ολοκλήρωση' : 'Checkout'}
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            {user ? (
              <div className="flex items-center gap-3">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" data-testid="user-avatar" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#1F2D50] flex items-center justify-center text-white text-sm font-bold" data-testid="user-avatar">
                    {user.name ? user.name.charAt(0).toUpperCase() : <User size={16} />}
                  </div>
                )}
                <button onClick={logout} className="hover:text-[#C8A951] transition" data-testid="logout-btn" aria-label="Logout">
                  <LogOut size={20} aria-hidden="true" />
                </button>
              </div>
            ) : (
              <button onClick={handleLogin} className="btn-secondary" data-testid="login-btn">
                <User size={16} className="inline mr-1.5" />
                {t.login}
              </button>
            )}
          </nav>

          {/* Mobile Menu Button & Cart */}
          <div className="flex md:hidden items-center gap-4">
            <Link to="/wishlist" className="relative hover:text-[#C8A951] transition" aria-label="Wishlist">
              <Heart size={22} className={wishlistIds.length > 0 ? 'fill-red-500 text-red-500' : ''} />
              {wishlistIds.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {wishlistIds.length}
                </span>
              )}
            </Link>
            <button onClick={() => openCartDrawer()} className="relative hover:text-[#C8A951] transition">
              <ShoppingCart size={24} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#C8A951] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {cartCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-700 hover:text-[#C8A951] transition"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={28} aria-hidden="true" /> : <Menu size={28} aria-hidden="true" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute left-0 right-0 top-full bg-white shadow-lg border-t z-50">
            <nav className="flex flex-col p-4 space-y-3">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-left py-2 hover:text-[#C8A951] transition">{t.home}</Link>
              <Link to="/shop" onClick={() => setIsMobileMenuOpen(false)} className="shop-link text-left w-full" style={{fontSize: '18px', padding: '12px 14px'}}>{t.shop}</Link>
              <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="text-left py-2 hover:text-[#C8A951] transition">{t.about}</Link>
              <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="text-left py-2 hover:text-[#C8A951] transition">{t.blog}</Link>
              <Link to="/faq" onClick={() => setIsMobileMenuOpen(false)} className="text-left py-2 hover:text-[#C8A951] transition">FAQ</Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="text-left py-2 hover:text-[#C8A951] transition">{t.contact}</Link>
              <Link to="/buyer/orders" onClick={() => setIsMobileMenuOpen(false)} className="text-left py-2 hover:text-[#C8A951] transition font-semibold">{t.myOrders}</Link>
              {user && user.role === 'admin' && <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)} className="text-left py-2 hover:text-[#C8A951] transition">{t.admin}</Link>}
              
              {/* Mobile Search */}
              <div className="pt-2 border-t">
                <form onSubmit={(e) => { e.preventDefault(); if (searchQuery.trim()) { navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`); setSearchQuery(''); setIsMobileMenuOpen(false); } }} className="flex gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === 'tr' ? 'Ürün ara...' : 'Search products...'}
                    className="flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-[#C8A951]"
                  />
                  <button type="submit" className="btn-primary px-3 py-2">
                    <Search size={18} />
                  </button>
                </form>
              </div>

              {/* Language Selector */}
              <div className="pt-2 border-t">
                <p className="text-sm font-semibold mb-2 text-gray-600">Language</p>
                <div className="space-y-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-100 transition ${
                        language === lang.code ? 'bg-gray-50 font-semibold' : ''
                      }`}
                    >
                      <span className="text-2xl">{lang.flag}</span>
                      <span>{lang.name}</span>
                      {language === lang.code && <span className="ml-auto text-[#C8A951]">✓</span>}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* User Section */}
              <div className="pt-2 border-t">
                {user ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#1F2D50] flex items-center justify-center text-white text-sm font-bold">
                          {user.name ? user.name.charAt(0).toUpperCase() : <User size={16} />}
                        </div>
                      )}
                      <span className="text-sm">{user.name}</span>
                    </div>
                    <button onClick={() => { logout(); setIsMobileMenuOpen(false); }} className="text-red-600 hover:text-red-700">
                      <LogOut size={20} />
                    </button>
                  </div>
                ) : (
                  <button onClick={handleLogin} className="btn-secondary w-full">
                    <User size={20} className="inline mr-2" />
                    {t.login}
                  </button>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;